import { Component, EventEmitter, inject, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { SwPartNumber } from '../../../../../shared/models/sw-part-number/sw-part-number';
import { SwPartNumberApiService } from '../../../../services/api/sw-part-number-api.service';
import { SwPartNumberService } from '../../../../services/sw-part-number.service';
import { FormsModule } from '@angular/forms';
import { NwNumberMajor } from '../../../../../shared/models/sw-part-number/nw-number-major';
import { Minor } from '../../../../../shared/models/sw-part-number/minor';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { prefixWithSlash } from '../../../../../shared/internal/prefix-with-slash';
import { ROUTE_PATHS } from '../../../../../shared/constants/route-paths';
import { CommonUtils } from '../../../../common/common-utils';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { UnsavedChangesTrackerService } from '../../../../services/unsaved-changes-tracker.service';

@Component({
  selector: 'app-sw-part-number-selection',
  imports: [FormsModule],
  templateUrl: './sw-part-number-selection.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sw-part-number-selection.component.scss',
})
export class SwPartNumberSelectionComponent implements OnInit, OnDestroy {
  @Output() public selectedSwPartNumber = new EventEmitter<string>();

  public swPartNumbers: SwPartNumber[] = [];
  public nwNumberMajors: NwNumberMajor[] = [];
  public minorParts: Minor[] = [];
  public selectedSwPartNumberId: string | null = null;
  public commonUtils = CommonUtils;
  public swPartNumberState = SwPartNumberState;

  public selectedNwNumberMajorPart?: string;

  private readonly swPartNumberApi = inject(SwPartNumberApiService);
  private readonly swPartNumberService = inject(SwPartNumberService);
  private readonly unsavedChangesService = inject(UnsavedChangesTrackerService);

  public ngOnInit(): void {
    this.fetchGroupedSwPartNumbers();
    window.addEventListener('storage', this.onStorageChange.bind(this));
    this.swPartNumberService.swPartNumberChangeSubject?.subscribe((swPartNumberId) => {
      if (swPartNumberId) {
        this.fetchGroupedSwPartNumbers();
      }
    });
  }

  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.onStorageChange.bind(this));
  }

  public onNwNumberMajorSelect(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedNwNumberMajorValue = selectElement.value;

    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(
      () => {
        this.handleNwNumberMajorSelection(selectedNwNumberMajorValue);
      },
      () => {
        const currentSwPartNumberId = this.swPartNumberService.getSwPartNumberId();
        const currentMajor = this.getNwNumberMajorPart(currentSwPartNumberId || '');
        this.selectedNwNumberMajorPart = currentMajor?.nwNumberWithMajorRelease;
        selectElement.value = this.selectedNwNumberMajorPart || '';
      }
    );
    if (!blocked) {
      this.handleNwNumberMajorSelection(selectedNwNumberMajorValue);
    }
  }

  public onMinorSelect(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedSwPartNumberId = selectElement.value;

    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(
      () => {
        this.handleSwPartNumberSelection(this.selectedSwPartNumberId || '');
      },
      () => {
        const currentSwPartNumberId = this.swPartNumberService.getSwPartNumberId();
        this.selectedSwPartNumberId = currentSwPartNumberId;
        selectElement.value = currentSwPartNumberId || '';
      }
    );

    if (!blocked) {
      this.handleSwPartNumberSelection(this.selectedSwPartNumberId || '');
    }
  }

  private onStorageChange(event: StorageEvent): void {
    if (event.key === STORAGE_KEYS.SW_PART_NUMBER && event.newValue) {
      const swPartNumberId = this.swPartNumberService.getSwPartNumberId();
      if (swPartNumberId) {
        this.selectedSwPartNumberId = swPartNumberId;
        this.handleSwPartNumberSelection(swPartNumberId);
      }
    }
  }

  private handleSwPartNumberSelection(selectedSwPartNumberId: string): void {
    this.selectedSwPartNumber.emit(selectedSwPartNumberId);
    this.swPartNumberService.setSwPartNumber(this.getSwPartNumber(selectedSwPartNumberId));
    this.selectedSwPartNumberId = selectedSwPartNumberId;
  }

  private fetchGroupedSwPartNumbers(): void {
    this.swPartNumberApi.getGroupedSwPartNumbers().subscribe({
      next: (groupedSwPartNumbers) => {
        this.swPartNumbers = groupedSwPartNumbers
          .flatMap((group) => group.swPartNumbers)
          .filter((swPartNumber): swPartNumber is SwPartNumber => swPartNumber !== undefined);
        this.nwNumberMajors = groupedSwPartNumbers
          .flatMap((group) => group.nwNumberMajorParts)
          .filter((nwNumberMajor): nwNumberMajor is NwNumberMajor => nwNumberMajor !== undefined);
        this.setNwNumberMajorAndMinorPart();
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  private setNwNumberMajorAndMinorPart(): void {
    let swPartNumberId = this.swPartNumberService.getSwPartNumberId();
    if (!swPartNumberId) {
      // fall back to the first SwPartNumber we can find:
      if (this.swPartNumbers.length > 0) {
        swPartNumberId = this.swPartNumbers[0].id || null;
      }

      if (swPartNumberId) {
        this.handleSwPartNumberSelection(swPartNumberId);
        // HINT: Otherwise sw part number is not set in api request, no better
        // solution found yet. Happens only if the user has never logged in before
        // or in incognito mode
        window.location.href = prefixWithSlash(ROUTE_PATHS.index);
      }
    }

    if (swPartNumberId) {
      const major = this.getNwNumberMajorPart(swPartNumberId);
      if (major) {
        this.selectedNwNumberMajorPart = major.nwNumberWithMajorRelease;
        this.minorParts = major.minorParts ?? [];
        this.selectedSwPartNumberId = swPartNumberId;
      }
    }
  }

  private handleNwNumberMajorSelection(selectedNwNumberMajorValue: string): void {
    const selectedNwNumberMajor = this.nwNumberMajors.find(
      (nwNumberMajor) => nwNumberMajor.nwNumberWithMajorRelease === selectedNwNumberMajorValue
    );
    this.minorParts = selectedNwNumberMajor?.minorParts ?? [];
    const swPartNumberId = this.minorParts[0]?.swPartNumberId;
    if (swPartNumberId) {
      this.selectedSwPartNumberId = swPartNumberId;
      this.selectedSwPartNumber.emit(swPartNumberId);
      this.swPartNumberService.setSwPartNumber(this.getSwPartNumber(swPartNumberId));
    }
  }

  private getSwPartNumber(id: string): SwPartNumber {
    const swPartNumber = this.swPartNumbers.find((swPartNumber) => swPartNumber.id === id);
    if (!swPartNumber) {
      throw new Error(`SwPartNumber with id ${id} not found`);
    }
    return swPartNumber;
  }

  private getNwNumberMajorPart(swPartNumberId: string): NwNumberMajor | undefined {
    return this.nwNumberMajors.find((nwNumberMajor) =>
      nwNumberMajor.minorParts?.some((swPartNumber) => swPartNumber.swPartNumberId === swPartNumberId)
    );
  }
}
